import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { kindIcon } from "@/pages/coach/methods/shared";
import { Plus, Play, Pencil, Copy, Trash2, Eye, CheckCircle2, Clock, Lock, Users, Globe, Bookmark, Layers } from "lucide-react";

const FILTERS = ["all", "shared", "template"];
const VIS_ICON = { private: Lock, shared_with_clients: Users, public: Globe };

export default function MethodsListTab({ refreshToken, onNew, onEdit, onPreview, onChanged }) {
  const t = useT();
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/coach/methods"); setRows(Array.isArray(data) ? data : []); }
    catch { setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load, refreshToken]);

  const filtered = (rows || []).filter((r) => (filter === "template" ? r.is_template : filter === "shared" ? r.visibility !== "private" : true));

  const duplicate = async (m) => {
    setBusy(true);
    try { await api.post(`/coach/methods/${m.id}/duplicate`); toast.success(t("cm.duplicated")); onChanged?.(); }
    catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };
  const remove = async (m) => {
    if (!window.confirm(t("cm.delete_confirm", { title: m.title }))) return;
    setBusy(true);
    try { await api.delete(`/coach/methods/${m.id}`); toast.success(t("cm.deleted")); onChanged?.(); }
    catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="cm-list-tab">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-1 rounded-full bg-secondary p-1">
          {FILTERS.map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`cm-filter-${f}`} className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${filter === f ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>{t(`cm.filter_${f}`)}</button>
          ))}
        </div>
        <Button className="gap-1.5 rounded-full" onClick={onNew} data-testid="cm-list-new"><Plus className="h-4 w-4" /> {t("cm.new_method")}</Button>
      </div>

      {rows === null ? (
        <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-32 rounded-2xl" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-foreground"><Layers className="h-6 w-6" /></div>
          <p className="mt-4 font-display text-lg text-foreground">{filter === "template" ? t("cm.no_templates") : t("cm.no_methods_title")}</p>
          <p className="mt-1 text-sm text-muted-foreground">{t("cm.no_methods_sub")}</p>
          <Button className="mt-4 gap-1.5 rounded-full" onClick={onNew} data-testid="cm-empty-cta"><Plus className="h-4 w-4" /> {t("cm.create_first")}</Button>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {filtered.map((m) => {
            const Icon = kindIcon(m.kind);
            const Vis = VIS_ICON[m.visibility] || Lock;
            return (
              <div key={m.id} data-testid={`cm-card-${m.id}`} className="flex flex-col rounded-2xl border border-border bg-card p-4">
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/15"><Icon className="h-5 w-5 text-primary" /></div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-foreground">{m.title}</p>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      <Pill>{t(`cm.kind_${m.kind}`)}</Pill>
                      {m.is_template && <Pill primary><Bookmark className="h-2.5 w-2.5" /> {t("cm.tpl")}</Pill>}
                      <Pill><Vis className="h-2.5 w-2.5" /> {t(`cm.vis_${m.visibility}`)}</Pill>
                      {m.price_credits > 0 && <Pill accent>{m.price_credits} Credits</Pill>}
                    </div>
                  </div>
                </div>
                {m.subtitle && <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{m.subtitle}</p>}
                <div className="mt-2 flex gap-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><Eye className="h-3.5 w-3.5" /> {m.opens_count}</span>
                  <span className="flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> {m.completions_count}</span>
                  {m.duration_min ? <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {m.duration_min}m</span> : null}
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5 border-t border-border pt-3">
                  <Button size="sm" variant="outline" className="h-8 gap-1 rounded-full" onClick={() => onPreview(m)} data-testid={`cm-preview-${m.id}`}><Play className="h-3.5 w-3.5" /> {m.preview_seconds || 15}s</Button>
                  <Button size="sm" variant="outline" className="h-8 gap-1 rounded-full" onClick={() => onEdit(m.id)} data-testid={`cm-edit-${m.id}`}><Pencil className="h-3.5 w-3.5" /> {t("cm.edit")}</Button>
                  <Button size="sm" variant="ghost" className="h-8 w-8 rounded-full p-0" disabled={busy} onClick={() => duplicate(m)} data-testid={`cm-dup-${m.id}`}><Copy className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="ghost" className="h-8 w-8 rounded-full p-0 text-rose-500 hover:text-rose-600" disabled={busy} onClick={() => remove(m)} data-testid={`cm-del-${m.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Pill({ children, primary, accent }) {
  const cls = primary ? "bg-primary text-primary-foreground" : accent ? "bg-emerald-100 text-emerald-700" : "bg-secondary text-muted-foreground";
  return <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${cls}`}>{children}</span>;
}
